import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoaderCircle, Mail, Plus, Trash2 } from "lucide-react";
import { usePlatformAuth } from "../../../lib/use-platform-auth";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { InlineMessage } from "../../../components/konto-helpers";

type OrganizationDetail = {
  id: string;
  name: string;
  slug: string;
  orgNumber?: string | null;
  billingAddress?: string | null;
  billingCity?: string | null;
  billingPostalCode?: string | null;
  invoicingMethod?: string | null;
  invoicingEmail?: string | null;
  bankAccountNumber?: string | null;
  shippingAddresses?: {
    label?: string;
    address: string;
    postalCode: string;
    city: string;
    contactPhone?: string;
  }[] | null;
};

export function SelskapTab() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { refresh } = usePlatformAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [bankAccountNumber, setBankAccountNumber] = useState<string | null>(null);
  const [pendingBankAccount, setPendingBankAccount] = useState("");
  const [savingBankAccount, setSavingBankAccount] = useState(false);
  const [bankAccountError, setBankAccountError] = useState<string | null>(null);
  const [bankAccountMessage, setBankAccountMessage] = useState<string | null>(null);

  const [orgName, setOrgName] = useState("");
  const [orgNumber, setOrgNumber] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingPostalCode, setBillingPostalCode] = useState("");
  const [invoicingMethod, setInvoicingMethod] = useState("email");
  const [invoicingEmail, setInvoicingEmail] = useState("");
  const [shippingAddresses, setShippingAddresses] = useState<{
    label?: string;
    address: string;
    postalCode: string;
    city: string;
    contactPhone?: string;
  }[]>([]);

  useEffect(() => {
    if (!organizationSlug) return;
    void fetchDetail(organizationSlug);
  }, [organizationSlug]);

  const fetchDetail = async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${slug}`, { credentials: "include" });
      if (!response.ok) throw new Error();
      const data = (await response.json()) as OrganizationDetail;
      setOrgName(data.name ?? "");
      setOrgNumber(data.orgNumber ?? "");
      setBillingAddress(data.billingAddress ?? "");
      setBillingCity(data.billingCity ?? "");
      setBillingPostalCode(data.billingPostalCode ?? "");
      setInvoicingMethod(data.invoicingMethod ?? "email");
      setInvoicingEmail(data.invoicingEmail ?? "");
      setShippingAddresses(data.shippingAddresses ?? []);
      setBankAccountNumber(data.bankAccountNumber ?? null);
    } catch {
      setError("Vi klarte ikke å hente selskapsinformasjonen.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationSlug) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${organizationSlug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: orgName,
          orgNumber,
          billingAddress,
          billingCity,
          billingPostalCode,
          invoicingMethod,
          invoicingEmail,
          shippingAddresses,
        }),
      });
      if (!response.ok) throw new Error();
      setMessage("Selskapsinformasjon er oppdatert.");
      await fetchDetail(organizationSlug);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke oppdatere selskapsinformasjon.");
    } finally {
      setSaving(false);
    }
  };

  const handleBankAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationSlug) return;
    setSavingBankAccount(true);
    setBankAccountError(null);
    setBankAccountMessage(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/organizations/${organizationSlug}/bank-account`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ bankAccountNumber: pendingBankAccount }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as any).error ?? "Kunne ikke sende kontonummer.");
      }
      setBankAccountMessage("Kontonummer er sendt til godkjenning.");
      setPendingBankAccount("");
    } catch (err) {
      setBankAccountError(err instanceof Error ? err.message : "Kunne ikke sende kontonummer.");
    } finally {
      setSavingBankAccount(false);
    }
  };

  return (
    <section
      className="rounded-3xl bg-white/50 p-8 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ border: "1px dashed var(--border)" }}
    >
      <div className="mb-10 space-y-3">
        <h2
          className="text-2xl font-black text-[#1a2e35]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Selskapsinfo
        </h2>
        <p className="text-base font-medium text-muted-foreground max-w-md leading-relaxed">
          Administrer organisasjonens identitet, fakturering og leveringsadresser.
        </p>
      </div>

      {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}
      {message ? <InlineMessage tone="success">{message}</InlineMessage> : null}

      {loading ? (
        <div
          className="flex h-32 items-center justify-center rounded-3xl"
          style={{ border: "1px dashed var(--border)", background: "var(--muted)/10" }}
        >
          <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : (
        <>
        <form onSubmit={handleSave} className="space-y-10">
          <div className="space-y-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-[#1a2e35]/40">Grunnleggende info</h3>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="orgName">Selskapsnavn</Label>
                <Input id="orgName" value={orgName} disabled className="opacity-60" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orgNumber">Organisasjonsnummer</Label>
                <Input id="orgNumber" value={orgNumber} disabled className="opacity-60" />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-[#1a2e35]/40">Fakturering</h3>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="billingAddress">Fakturaadresse</Label>
                  <Input id="billingAddress" value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="Storgata 1" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="billingPostalCode">Postnummer</Label>
                    <Input id="billingPostalCode" value={billingPostalCode} onChange={(e) => setBillingPostalCode(e.target.value)} placeholder="0123" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billingCity">Sted</Label>
                    <Input id="billingCity" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} placeholder="Oslo" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Faktureringsmetode</Label>
                  <div className="flex gap-4">
                    {[{ id: "email", label: "E-post" }, { id: "ehf", label: "EHF" }].map((method) => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setInvoicingMethod(method.id)}
                        className={`flex h-12 flex-1 items-center justify-center rounded-xl border px-4 text-center transition-all ${
                          invoicingMethod === method.id
                            ? "border-accent bg-accent/5 text-accent ring-1 ring-accent"
                            : "border-border bg-white text-muted-foreground hover:border-accent/30"
                        }`}
                      >
                        <span className="text-xs font-black uppercase tracking-widest">{method.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invoicingEmail">E-post for faktura/purringer</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/40" />
                    <Input
                      id="invoicingEmail"
                      type="email"
                      value={invoicingEmail}
                      onChange={(e) => setInvoicingEmail(e.target.value)}
                      className="pl-10"
                      placeholder="faktura@firma.no"
                    />
                  </div>
                  <p className="text-[10px] font-medium text-muted-foreground">
                    Påkrevd selv ved bruk av EHF for å motta purringer og varsler.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-widest text-[#1a2e35]/40">Leveringsadresser</h3>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShippingAddresses([...shippingAddresses, { label: "", address: "", postalCode: "", city: "", contactPhone: "" }])}
                className="h-8 rounded-lg px-3 text-[10px] font-black uppercase tracking-widest"
              >
                <Plus className="mr-1 h-3 w-3" />
                Legg til adresse
              </Button>
            </div>

            {shippingAddresses.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/5 p-8 text-center">
                <p className="text-xs font-medium text-muted-foreground">Ingen leveringsadresser lagt til.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {shippingAddresses.map((addr, idx) => (
                  <div key={idx} className="group relative rounded-2xl bg-white p-5 shadow-sm ring-1 ring-border transition-all hover:shadow-md">
                    <button
                      type="button"
                      onClick={() => setShippingAddresses(shippingAddresses.filter((_, i) => i !== idx))}
                      className="absolute right-3 top-3 rounded-lg p-1 text-muted-foreground/40 opacity-0 transition-opacity hover:bg-destructive/5 hover:text-destructive group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-black uppercase tracking-widest opacity-40">Navn/Label</Label>
                          <Input
                            value={addr.label}
                            onChange={(e) => {
                              const next = [...shippingAddresses];
                              next[idx].label = e.target.value;
                              setShippingAddresses(next);
                            }}
                            placeholder="F.eks. Hovedlager"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-black uppercase tracking-widest opacity-40">Kontakt (Tlf)</Label>
                          <Input
                            value={addr.contactPhone}
                            onChange={(e) => {
                              const next = [...shippingAddresses];
                              next[idx].contactPhone = e.target.value;
                              setShippingAddresses(next);
                            }}
                            placeholder="Tlf v/levering"
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-black uppercase tracking-widest opacity-40">Adresse</Label>
                        <Input
                          value={addr.address}
                          onChange={(e) => {
                            const next = [...shippingAddresses];
                            next[idx].address = e.target.value;
                            setShippingAddresses(next);
                          }}
                          placeholder="Gateveien 1"
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-black uppercase tracking-widest opacity-40">Postnr.</Label>
                          <Input
                            value={addr.postalCode}
                            onChange={(e) => {
                              const next = [...shippingAddresses];
                              next[idx].postalCode = e.target.value;
                              setShippingAddresses(next);
                            }}
                            placeholder="0000"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-black uppercase tracking-widest opacity-40">Sted</Label>
                          <Input
                            value={addr.city}
                            onChange={(e) => {
                              const next = [...shippingAddresses];
                              next[idx].city = e.target.value;
                              setShippingAddresses(next);
                            }}
                            placeholder="Sted"
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="rounded-xl px-10 py-6 text-base font-bold shadow-lg shadow-accent/20 transition-all hover:scale-105 active:scale-95"
            >
              {saving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Lagre selskapsinfo
            </Button>
          </div>
        </form>

        <form onSubmit={handleBankAccountSubmit} className="mt-10 space-y-6 border-t border-dashed border-border pt-10">
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-[#1a2e35]/40">Utbetalingskonto</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Registrer kontonummeret som skal brukes for utbetalinger. Kontonummer må godkjennes av administrator før det aktiveres.
            </p>
          </div>

          {bankAccountError ? <InlineMessage tone="error">{bankAccountError}</InlineMessage> : null}
          {bankAccountMessage ? <InlineMessage tone="success">{bankAccountMessage}</InlineMessage> : null}

          <div className="space-y-2">
            <Label>Aktivt kontonummer</Label>
            <p className="font-mono text-sm text-muted-foreground">
              {bankAccountNumber ?? "Ikke registrert"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pendingBankAccount">Send inn nytt kontonummer</Label>
            <Input
              id="pendingBankAccount"
              value={pendingBankAccount}
              onChange={(e) => setPendingBankAccount(e.target.value)}
              placeholder="11 siffer"
              maxLength={13}
            />
            <p className="text-[10px] font-medium text-muted-foreground">
              Norsk kontonummer, 11 siffer. Venter på godkjenning fra administrator.
            </p>
          </div>

          <Button
            type="submit"
            disabled={savingBankAccount || !pendingBankAccount}
            className="rounded-xl px-8 py-5 text-sm font-bold"
          >
            {savingBankAccount ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send til godkjenning
          </Button>
        </form>
        </>
      )}
    </section>
  );
}
